import React from 'react';
import { ProcessedValidationError } from '../types';
import Button from 'shared/components/UC/Button';
import styles from './ValidationErrorDisplay.module.css';

interface ValidationErrorDisplayProps {
  errors: ProcessedValidationError[];
  onNavigateToSection?: (sectionIndex: number) => void;
  onClose?: () => void;
  steps?: { id: string; title: string }[];
}

const ValidationErrorDisplay: React.FC<ValidationErrorDisplayProps> = ({
  errors,
  onNavigateToSection,
  onClose,
  steps = [],
}) => {
  if (!errors || errors.length === 0) {
    return null;
  }

  // Group errors by section
  const errorsBySection = errors.reduce(
    (acc, error) => {
      const section = error.section;
      if (!acc[section]) {
        acc[section] = [];
      }
      acc[section].push(error);
      return acc;
    },
    {} as Record<string, ProcessedValidationError[]>
  );

  // Helper function to find section index
  const findSectionIndex = (sectionId: string) => {
    return steps.findIndex((step) => step.id === sectionId);
  };

  return (
    <div className={styles.errorContainer}>
      <div className={styles.errorHeader}>
        <div>
          <h3 className={styles.errorTitle}>
            ⚠️ Se encontraron {errors.length} error(es) en su postulación
          </h3>
          <p className={styles.errorSubtitle}>
            Por favor, corrija los siguientes campos para poder enviar su
            postulación:
          </p>
        </div>
        {onClose && (
          <Button
            type="button"
            variant="danger"
            onClick={onClose}
            style={{ alignSelf: 'flex-start' }}
          >
            ✕
          </Button>
        )}
      </div>

      <div className={styles.errorSections}>
        {Object.entries(errorsBySection).map(([sectionKey, sectionErrors]) => {
          const sectionTitle = sectionErrors[0]?.sectionTitle || sectionKey;
          const sectionIndex = findSectionIndex(sectionKey);

          return (
            <div key={sectionKey} className={styles.errorSection}>
              <div className={styles.sectionHeader}>
                <h4 className={styles.sectionTitle}>
                  {sectionTitle} ({sectionErrors.length} error
                  {sectionErrors.length > 1 ? 'es' : ''})
                </h4>
                {onNavigateToSection && sectionIndex !== -1 && (
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => onNavigateToSection(sectionIndex)}
                  >
                    Ir a sección
                  </Button>
                )}
              </div>

              <ul className={styles.errorList}>
                {sectionErrors.map((error, index) => (
                  <li key={index} className={styles.errorItem}>
                    <span className={styles.errorField}>
                      {error.fieldLabel}:
                    </span>
                    <span className={styles.errorMessage}>{error.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <div className={styles.errorFooter}>
        <p className={styles.errorNote}>
          Una vez corregidos todos los errores, podrá enviar su postulación.
        </p>
      </div>
    </div>
  );
};

export default ValidationErrorDisplay;
